import type { ChangeEvent, ReactNode } from "react";

import { useHover } from "@react-aria/interactions";

import { SWReactIcons } from "@similarweb/icons";
import {
    ScrollArea,
    ListItemKeyword,
    ListItemKeywordGroup,
    ListItemKeywordGroupShared,
} from "@similarweb/ui-components";

import {
    NoResultsContainer,
    InnerTitleContainer,
} from "pro-features/components/AutocompleteKeywords/styles/AutocompleteKeywordStyles";
import type {
    IAutocompleteKeyword,
    IAutocompleteKeywordGroup,
} from "pro-features/components/AutocompleteKeywords/types/AutocompleteKeywordGroupTypes";
import { keywordsGroupService } from "app/services/keywordsGroup/keywordsGroupService";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";

const ScrollAreaWrap = ({ children, ...rest }: FixmeType) => {
    return (
        <ScrollArea
            style={{ maxHeight: 400, minHeight: 0 }}
            verticalScrollbarStyle={{ borderRadius: 5 }}
            horizontal={false}
            smoothScrolling={true}
            minScrollSize={48}
            {...rest}
        >
            {children}
        </ScrollArea>
    );
};

export const NoKeywordResults = (props: { title: string }) => {
    return (
        <NoResultsContainer>
            <SWReactIcons
                iconName={"no-search-results"}
                size={"sm"}
            />
            <span>{props.title}</span>
        </NoResultsContainer>
    );
};

export const KeywordsSection = (props: {
    keywords?: IAutocompleteKeyword[];
    isRecentSearches: boolean;
    renderOnItemClick: (item: IAutocompleteKeyword | string[]) => (e: ChangeEvent<HTMLInputElement> | Event) => void;
    selectedItemId: number;
    showAutoCreateKeywordGroupButton: boolean;
    translate: (key: string) => string;
    onAutoCreateKeywordGroupClick: (item: FixmeType) => void;
}) => {
    const {
        keywords,
        isRecentSearches,
        showAutoCreateKeywordGroupButton,
        selectedItemId,
        translate,
        renderOnItemClick,
        onAutoCreateKeywordGroupClick,
    } = props;

    const { hoverProps, isHovered } = useHover({});

    const renderKeywordItem = (item: IAutocompleteKeyword, index: number) => {
        return (
            // @ts-ignore FIXME: strict migration - discriminated union from @similarweb/ui-components expects exact prop combinations
            <ListItemKeyword
                text={item.name}
                iconName="search-keywords"
                isActive={!isHovered ? selectedItemId === index : undefined}
                {...(showAutoCreateKeywordGroupButton ? { hasAutoCreateKeywordGroupButton: true as const } : {})}
                createKeywordGroupButtonText={translate("listItemKeyword.auto.create.keyword.list.button")}
                onAutoCreateKeywordGroupButtonClick={() => onAutoCreateKeywordGroupClick(item)}
                onClick={renderOnItemClick(item)}
            />
        );
    };

    return !keywords || keywords.length === 0 ? (
        <NoKeywordResults title={translate("autocompleteKeywordsGroup.noKeywordsFound")} />
    ) : (
        <ScrollAreaWrap>
            {isRecentSearches ? (
                <InnerTitleContainer>
                    {translate("autocompleteKeywordsGroup.keywordsTab.recentSearches")}
                </InnerTitleContainer>
            ) : null}
            {keywords.map((item, index) => (
                <div
                    {...hoverProps}
                    key={`${item.name}_${index}`}
                    data-automation-item={"ListItemKeyword"}
                >
                    {renderKeywordItem(item, index)}
                </div>
            ))}
        </ScrollAreaWrap>
    );
};

export const KeywordsGroupSection = (props: {
    keywordGroups: IAutocompleteKeywordGroup[];
    sharedKeywordGroups: IAutocompleteKeywordGroup[];
    selectedItemId: number;
    translate: (key: string) => string;
    renderOnItemClick: (item: IAutocompleteKeywordGroup) => (e: ChangeEvent<HTMLInputElement>) => void;
    onEditClick: (item: FixmeType) => void;
    onShareClick: (item: FixmeType) => void;
    showEditButton?: boolean;
    showShareButton?: boolean;
    children?: ReactNode;
}) => {
    const {
        keywordGroups,
        sharedKeywordGroups,
        selectedItemId,
        translate,
        renderOnItemClick,
        onEditClick,
        onShareClick,
        showEditButton = true,
        showShareButton = true,
        children,
    } = props;
    const { hoverProps, isHovered } = useHover({});

    const hasKeywordGroups = keywordGroups && keywordGroups.length > 0;
    const hasSharedKeywordGroups = sharedKeywordGroups && sharedKeywordGroups.length > 0;

    const hasGroupsData = hasKeywordGroups || hasSharedKeywordGroups;
    if (!hasGroupsData && !children) {
        return <NoKeywordResults title={translate("autocompleteKeywordsGroup.noKeywordGroupsFound")} />;
    }

    const renderGroupComponent = (item: IAutocompleteKeywordGroup, index: number) => {
        const Component = item.SharedWithMe ? ListItemKeywordGroupShared : ListItemKeywordGroup;
        const showEdit = showEditButton && !item.SharedWithMe;
        const showShare = keywordsGroupService.canShareKeywordsGroup() && showShareButton && !item.SharedWithMe;
        return (
            <div
                {...hoverProps}
                key={`${item.Id}_${selectedItemId === index}`}
            >
                <Component
                    text={item.Name}
                    isActive={!isHovered ? selectedItemId === index : undefined}
                    hasEditButton={showEdit}
                    hasShareButton={showShare}
                    onEditButtonClick={() => onEditClick(item)}
                    onShareButtonClick={() => onShareClick(item)}
                    onClick={renderOnItemClick(item)}
                />
            </div>
        );
    };

    const myGroupsComponent = hasKeywordGroups ? (
        <FlexColumn>
            <InnerTitleContainer>
                {translate("autocompleteKeywordsGroup.keywordsGroupTab.myGroups")}
            </InnerTitleContainer>
            {keywordGroups.map(renderGroupComponent)}
        </FlexColumn>
    ) : null;

    const sharedGroupsComponent = hasSharedKeywordGroups ? (
        <FlexColumn>
            <InnerTitleContainer>
                {translate("autocompleteKeywordsGroup.keywordsGroupTab.sharedGroups")}
            </InnerTitleContainer>
            {sharedKeywordGroups.map((item, index) =>
                // index of shared keyword group is the number of keyword groups + i (because they come after)
                renderGroupComponent(item, keywordGroups.length + index),
            )}
        </FlexColumn>
    ) : null;

    return (
        <ScrollAreaWrap>
            {children}
            {myGroupsComponent}
            {sharedGroupsComponent}
        </ScrollAreaWrap>
    );
};
